import type { Achievement, UserContext } from "./victoryAchievements";

/**
 * Renders a 1080x1920 portrait PNG suitable for WhatsApp/IG/TikTok stories.
 * Pure canvas — no server, no mock data. Returns a data URL.
 */

const W = 1080;
const H = 1920;

const ACCENT_BY_TYPE: Record<Achievement["type"], string> = {
  first_correct: "#22c55e",
  new_badge: "#f59e0b",
  top_predictor: "#fbbf24",
  nation_leader: "#22d3ee",
  top_10pct: "#fb923c",
  top_1pct: "#a78bfa",
  streak: "#ef4444",
  national_victory: "#10b981",
};

function hexToRgb(hex: string) {
  const h = hex.replace("#", "");
  const n = parseInt(h, 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

function wrap(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  const words = text.split(/\s+/);
  const lines: string[] = [];
  let line = "";
  for (const w of words) {
    const test = line ? `${line} ${w}` : w;
    if (ctx.measureText(test).width > maxWidth && line) {
      lines.push(line);
      line = w;
    } else line = test;
  }
  if (line) lines.push(line);
  return lines;
}

async function loadImage(src: string): Promise<HTMLImageElement | null> {
  try {
    return await new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = src;
    });
  } catch {
    return null;
  }
}

export async function renderVictoryCard(ach: Achievement, ctx: UserContext): Promise<string> {
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const c = canvas.getContext("2d");
  if (!c) throw new Error("canvas-unavailable");

  const accent = ACCENT_BY_TYPE[ach.type] ?? "#e11d48";
  const { r, g, b } = hexToRgb(accent);

  // Background
  const bg = c.createLinearGradient(0, 0, 0, H);
  bg.addColorStop(0, "#0a0a1a");
  bg.addColorStop(1, "#150a1f");
  c.fillStyle = bg;
  c.fillRect(0, 0, W, H);

  // Accent glow
  const glow = c.createRadialGradient(W / 2, H * 0.32, 60, W / 2, H * 0.32, W * 0.9);
  glow.addColorStop(0, `rgba(${r},${g},${b},0.55)`);
  glow.addColorStop(1, "rgba(0,0,0,0)");
  c.fillStyle = glow;
  c.fillRect(0, 0, W, H);

  // Top bar — brand
  c.fillStyle = "rgba(255,255,255,0.75)";
  c.font = "600 28px system-ui, -apple-system, Segoe UI, Roboto";
  c.textAlign = "left";
  c.fillText("VerseSport · Mundial 2026", 80, 100);

  // Identity row: avatar + name + flag/nation
  const avatar = ctx.avatar_url ? await loadImage(ctx.avatar_url) : null;
  const idTop = 160;
  const avatarSize = 120;
  if (avatar) {
    c.save();
    c.beginPath();
    c.arc(80 + avatarSize / 2, idTop + avatarSize / 2, avatarSize / 2, 0, Math.PI * 2);
    c.closePath();
    c.clip();
    c.drawImage(avatar, 80, idTop, avatarSize, avatarSize);
    c.restore();
    c.strokeStyle = `rgba(${r},${g},${b},0.9)`;
    c.lineWidth = 4;
    c.beginPath();
    c.arc(80 + avatarSize / 2, idTop + avatarSize / 2, avatarSize / 2, 0, Math.PI * 2);
    c.stroke();
  } else {
    c.fillStyle = "rgba(255,255,255,0.08)";
    c.beginPath();
    c.arc(80 + avatarSize / 2, idTop + avatarSize / 2, avatarSize / 2, 0, Math.PI * 2);
    c.fill();
    c.fillStyle = "rgba(255,255,255,0.5)";
    c.font = "700 56px system-ui";
    c.textAlign = "center";
    c.textBaseline = "middle";
    c.fillText((ctx.display_name?.[0] ?? "F").toUpperCase(), 80 + avatarSize / 2, idTop + avatarSize / 2 + 4);
    c.textBaseline = "alphabetic";
  }

  c.textAlign = "left";
  c.fillStyle = "#ffffff";
  c.font = "800 44px system-ui";
  c.fillText(ctx.display_name ?? "Fan", 230, idTop + 55);
  c.fillStyle = "rgba(255,255,255,0.7)";
  c.font = "500 32px system-ui, 'Apple Color Emoji', 'Segoe UI Emoji'";
  const nationLabel = ctx.nation_name ? `${ctx.nation_flag ?? "🏳️"} ${ctx.nation_name}` : "Sin nación";
  c.fillText(nationLabel, 230, idTop + 100);

  // Hero emoji
  c.textAlign = "center";
  c.font = "260px system-ui, 'Apple Color Emoji', 'Segoe UI Emoji'";
  c.fillText(ach.emoji, W / 2, 720);

  // Headline
  c.fillStyle = "#ffffff";
  c.font = "900 96px system-ui, -apple-system, Segoe UI, Roboto";
  const headLines = wrap(c, ach.headline, W - 160).slice(0, 3);
  let y = 900;
  for (const ln of headLines) {
    c.fillText(ln, W / 2, y);
    y += 110;
  }

  // Subline
  if (ach.subline) {
    c.fillStyle = "rgba(255,255,255,0.8)";
    c.font = "500 48px system-ui";
    const sub = wrap(c, ach.subline, W - 200).slice(0, 2);
    y += 20;
    for (const ln of sub) {
      c.fillText(ln, W / 2, y);
      y += 64;
    }
  }

  // Stats strip
  const statsTop = 1480;
  c.fillStyle = "rgba(255,255,255,0.05)";
  c.fillRect(60, statsTop, W - 120, 220);
  c.strokeStyle = `rgba(${r},${g},${b},0.35)`;
  c.lineWidth = 2;
  c.strokeRect(60, statsTop, W - 120, 220);

  const cells: Array<{ label: string; value: string }> = [
    { label: "XP", value: ctx.total_points.toLocaleString() },
    { label: "Racha", value: String(ctx.streak_best) },
    { label: "Aciertos", value: String(ctx.exact_predictions) },
    {
      label: "Ranking",
      value: ctx.global_rank ? `#${ctx.global_rank}` : "—",
    },
  ];
  const cellW = (W - 120) / cells.length;
  cells.forEach((cell, i) => {
    const cx = 60 + cellW * i + cellW / 2;
    c.fillStyle = "#ffffff";
    c.font = "900 64px system-ui";
    c.fillText(cell.value, cx, statsTop + 110);
    c.fillStyle = "rgba(255,255,255,0.55)";
    c.font = "600 26px system-ui";
    c.fillText(cell.label.toUpperCase(), cx, statsTop + 165);
  });

  // Footer brand
  c.fillStyle = `rgba(${r},${g},${b},1)`;
  c.font = "900 58px system-ui";
  c.fillText("VerseSport", W / 2, H - 120);
  c.fillStyle = "rgba(255,255,255,0.55)";
  c.font = "500 32px system-ui";
  c.fillText("versesport.com", W / 2, H - 70);

  return canvas.toDataURL("image/png");
}

export async function dataUrlToFile(dataUrl: string, filename: string): Promise<File> {
  const blob = await (await fetch(dataUrl)).blob();
  return new File([blob], filename, { type: "image/png" });
}

export function downloadDataUrl(dataUrl: string, filename: string) {
  const a = document.createElement("a");
  a.href = dataUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
}
